import React from 'react';
import { useTranslation } from 'react-i18next';
import useRevealOnScroll from '../../hooks/useRevealOnScroll';
import BackButton from '../../components/BackButton';
import image1 from '../../images/forensic/1.png';
import image2 from '../../images/forensic/2.png';
import image3 from '../../images/forensic/3.png';
import image4 from '../../images/forensic/4.png';
import image5 from '../../images/forensic/5.png';
import image6 from '../../images/forensic/6.png';
import image7 from '../../images/forensic/7.png';
import image8 from '../../images/forensic/8.png';
import image9 from '../../images/forensic/9.png';
import image10 from '../../images/forensic/10.png';
import image11 from '../../images/forensic/11.png';

const tints = ['0,194,255', '0,112,210', '163,230,53'];

function ItemRow({ idx, item, image, reversed, tint }) {
  const [revealRef, isVisible] = useRevealOnScroll(0.1);

  const handleSpotlight = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <div
      ref={revealRef}
      className={`reveal group flex flex-col md:items-center gap-8 md:gap-16 py-14 sm:py-16 ${
        reversed ? 'md:flex-row-reverse' : 'md:flex-row'
      } ${isVisible ? 'is-visible' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <span
          className="font-display font-extrabold text-white/[0.04] block leading-none select-none text-[96px] sm:text-[120px] -mb-8 sm:-mb-10"
          aria-hidden="true"
        >
          {String(idx + 1).padStart(2, '0')}
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4 relative">
          {item.name}
        </h3>
        <p className="text-white/60 leading-relaxed max-w-lg relative">
          {item.description}
        </p>
      </div>

      <div
        onMouseMove={handleSpotlight}
        className="spotlight glass-card flex-none w-full md:w-[380px] h-[220px] sm:h-[260px] rounded-3xl relative overflow-hidden transition-colors duration-300 group-hover:border-cyan-400/40 flex items-center justify-center"
      >
        <img
          src={image}
          alt={item.name}
          className="relative z-10 max-h-[70%] max-w-[80%] object-contain group-hover:scale-105 transition-transform duration-500"
        />
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at ${reversed ? '70%' : '30%'} 25%, rgba(${tint},0.2), transparent 60%)`,
          }}
        />
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-2">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{children}</h2>
      <span className="title-accent is-visible" />
    </div>
  );
}

export default function DigitalForensics() {
  const { t } = useTranslation('digitalforensics');

  const softwareTools = t('software', { returnObjects: true });
  const hardwareTools = t('hardware', { returnObjects: true });

  const softwareImages = [image1, image2, image3, image4, image5];
  const hardwareImages = [image6, image7, image8, image9, image10, image11];

  return (
    <section className="relative min-h-screen text-white bg-gradient-to-b from-[#070b15] to-[#0a111f] pt-32 pb-24 overflow-hidden">
      <div className="grid-bg opacity-60" />
      <div className="glow-orb w-[560px] h-[560px] bg-[#0070d2] opacity-20 -top-40 -left-32 [animation:drift-slow_24s_ease-in-out_infinite,glow-pulse_9s_ease-in-out_infinite]" />
      <div className="glow-orb w-[480px] h-[480px] bg-[#00c2ff] opacity-20 top-1/3 right-[-160px] [animation:drift_15s_ease-in-out_infinite_2s,glow-pulse_9s_ease-in-out_infinite_1.5s]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackButton label={t('back')} to="/" hash="#services" />

        <div className="max-w-2xl">
          <p className="font-mono text-xs tracking-[0.3em] text-white/40 mb-2">
            01&ndash;{String(softwareTools.length + hardwareTools.length).padStart(2, '0')}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white">
            {t('title')}
          </h1>
          <span className="title-accent is-visible" />
          <p className="text-white/60 text-lg mt-6">
            {t('subtitle')}
          </p>
        </div>

        <div className="mt-16">
          <SectionTitle>{t('software_title')}</SectionTitle>
          <div className="divide-y divide-white/10">
            {softwareTools.map((item, idx) => (
              <ItemRow
                key={item.name}
                idx={idx}
                item={item}
                image={softwareImages[idx]}
                reversed={idx % 2 === 1}
                tint={tints[idx % tints.length]}
              />
            ))}
          </div>
        </div>

        <div className="mt-8">
          <SectionTitle>{t('hardware_title')}</SectionTitle>
          <div className="divide-y divide-white/10">
            {hardwareTools.map((item, idx) => (
              <ItemRow
                key={item.name}
                idx={softwareTools.length + idx}
                item={item}
                image={hardwareImages[idx]}
                reversed={idx % 2 === 1}
                tint={tints[idx % tints.length]}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
